import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { listMeetings } from '../../api/meetings/meetings';
import { useAppointmentUpdates } from '../../hooks/useAppointmentUpdates';
import MeetingsQuickActionsColumn from './MeetingsQuickActionsColumn';
import TodayMeetingsCard from './TodayMeetingsCard';
import PendingMeetingsCard from './PendingMeetingsCard';
import RecentUpdatesCard from './RecentUpdatesCard';
import UpcomingMeetingsCard from './UpcomingMeetingsCard';
import './styles/OverviewCards.css';

const convertToDate = (dateValue) => {
  if (!dateValue) return null;
  try {
    if (dateValue instanceof Date) return dateValue;
    if (dateValue?.toDate && typeof dateValue.toDate === 'function') return dateValue.toDate();
    if (dateValue?.seconds) return new Date(dateValue.seconds * 1000);
    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
};

const MeetingsOverview = () => {
  const navigate = useNavigate();
  const { companyData } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const { updates, loading: updatesLoading, markUpdatesReadLocal } = useAppointmentUpdates(companyData?.id);

  useEffect(() => {
    if (!companyData?.id) return undefined;

    let cancelled = false;
    const loadMeetings = async () => {
      try {
        const rows = await listMeetings({ companyId: companyData.id, archived: false });
        if (cancelled) return;
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        const list = rows
          .map((data) => {
            const dateTime = convertToDate(data.dateTime);
            if (!dateTime) return null;
            return { ...data, dateTime };
          })
          .filter((m) => {
            if (!m) return false;
            const status = (m.status || '').toLowerCase();
            if (status === 'cancelled' || status === 'canceled') return false;
            if (m.archived || m.archivedAt) return false;
            const day = new Date(m.dateTime);
            day.setHours(0, 0, 0, 0);
            return day >= now;
          })
          .sort((a, b) => a.dateTime - b.dateTime);
        setMeetings(list);
      } catch (error) {
        console.error('Error loading meetings overview:', error);
      }
    };

    loadMeetings();
    const poll = setInterval(loadMeetings, 12000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [companyData?.id]);

  const todayMeetings = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return meetings.filter((m) => {
      const t = m.dateTime instanceof Date ? m.dateTime : new Date(m.dateTime);
      return t >= today && t < tomorrow;
    });
  }, [meetings]);

  const pendingMeetings = useMemo(
    () => meetings.filter((m) => (m.status || '').toLowerCase() === 'pending'),
    [meetings]
  );

  const handleUpdateRead = (updateId) => {
    markUpdatesReadLocal(updateId);
  };

  const handleClearAllRead = (updateIds) => {
    markUpdatesReadLocal(updateIds);
  };

  const handleMeetingClick = (meeting) => {
    if (!meeting?.id) return;
    const meetingSegment = meeting.shortSlug ?? meeting.id;
    navigate(`/app/overview/meeting/${meetingSegment}`, { state: { meeting } });
  };

  return (
    <div className="overview-cards-container">
      <div className="overview-card quick-actions-card">
        <MeetingsQuickActionsColumn />
      </div>
      <div className="overview-card today-appointments-card overview-card--mobile-nav-target">
        <TodayMeetingsCard
          todayMeetings={todayMeetings}
          onViewAll={() => navigate('/app/overview/meetings/list', { state: { initialTab: 1 } })}
          onMeetingClick={handleMeetingClick}
        />
      </div>
      <div className="overview-card pending-confirmations-card overview-card--mobile-nav-target">
        <PendingMeetingsCard
          pendingMeetings={pendingMeetings}
          onViewAll={() => navigate('/app/overview/meetings/list', { state: { initialTab: 2 } })}
          onMeetingClick={handleMeetingClick}
        />
      </div>
      <div className="overview-card recent-updates-card overview-card--mobile-nav-target">
        <RecentUpdatesCard
          updates={updates}
          loading={updatesLoading}
          companyId={companyData?.id}
          onUpdateRead={handleUpdateRead}
          onClearAllRead={handleClearAllRead}
        />
      </div>

      <div className="overview-card overview-card-full upcoming-appointments-full-width">
        <UpcomingMeetingsCard
          meetings={meetings}
          onMeetingClick={handleMeetingClick}
        />
      </div>
    </div>
  );
};

export default MeetingsOverview;
